
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import Moment from 'moment';
import SafariBrowserDetector from '@/components/mixin/SafariBrowserDetector.js';
import ModalAlert from '@/components/ModalAlert';
import CountriesSelect from '@/components/SettingsCountriesSelect';
import scrollTo from '@/_helpers/scrollTo.js';
import vmodules from './vmodules';
import modalRemove from '@/components/vacancies/VacancyModalRemove';

@Component({
  name: 'vacancy-settings',
  components: {
    ModalAlert,
    CountriesSelect,
    vmodules,
    modalRemove
  },
  mixins: [SafariBrowserDetector]
})
export default class VacancySettings extends Vue {
  @Prop() readonly modules: [];

  private modalOpen = false;
  private blockedSaveVacancy = false;
  private loading = false;
  private modal_vac = false;
  private force_send = false;
  private preview_url = false;

  @Watch('vacancy.id')
  vacancyIdChanged(val) {
    this.$router.push({
      name: 'vacancy_basic',
      params: {
        id: val
      }
    });
  }

  @Watch('isDeleted')
  isDeletedChanged(val) {
    if (val) {
      this.$router.push({ name: 'vacancies' });
    }
  }

  @Watch('has_changes')
  hasChangesChanged(val) {
    if (val) {
      window.onbeforeunload = function (e) {
        return e;
      };
    } else {
      window.onbeforeunload = null;
    }
  }

  public get_vacancy(id) {
    this.$store.dispatch('vacancy_new/load', id).then(() => {
      scrollTo(0);
      this.successMessage();
    });
  }

  public onModalClosed() {
    this.modalOpen = false;
  }

  public copyVacancy() {
    this.$store
      .dispatch('vacancy/copyOne', this.vacancy)
      .then(data => {
        const id = data ? data.id : null;
        this.go_to_copied_vacancy(id);
      })
      .catch(this.failureMessage);
  }

  public archiveVacancy() {
    this.$store.dispatch('vacancy/archive', this.vacancy).then(this.successMessage).catch(this.failureMessage);
  }

  public restore() {
    this.$store
      .dispatch('vacancy/restore', this.vacancy)
      .then(this.successMessage)
      .then(() => {
        this.$router.push({ name: 'vacancies-archive' });
      })
      .catch(this.failureMessage);
  }

  public deleteVacancy() {
    if (this.has_responses) {
      this.set_force(true);
    }

    this.$store
      .dispatch('vacancy/deleteOne', {
        id: this.vacancy.id,
        force: this.force_send
      })
      .then(() => {
        this.$noty.success(this.$tr('vacancies.delete.result', 1, { is_pluralization: true }));
        this.$router.push({ name: 'vacancies' });
        this.set_force(false);
      })
      .catch(() => {
        this.failureMessage();
        this.set_force(false);
      });
  }

  public remove() {
    this.toggle_modal();
    this.deleteVacancy();
  }

  public toggle_modal() {
    this.modal_vac = !this.modal_vac;
  }

  public set_force(val) {
    this.force_send = val;
  }

  public setResumeMandatory(val) {
    this.isResumeMandatory = val;
  }

  public errSave(val) {
    if (!val) {
      this.blockedSaveVacancy = true;
    } else {
      this.blockedSaveVacancy = false;
    }
  }

  public setChange(val) {
    this.$store.commit('vacancy/setChange', val);
  }

  public save(is_continue = 'false') {
    this.loading = true;
    this.setChange(true);

    this.$store
      .dispatch('vacancy/save', this.vacancy)
      .then(() => {
        this.loading = false;
        this.setChange(false);
        this.successMessage();

        if (is_continue) {
          this.go_to_next_section();
        }
      })
      .catch(() => {
        this.loading = false;
        this.failureMessage();
      });
  }

  public go_to_next_section() {
    this.$router.push({ path: `/vacancies/${this.vacancyId}/landing` });
  }

  public successMessage() {
    this.$noty.success(this.$tr('vacancy.preferences.modals.saveSuccess.text'));
  }

  public failureMessage() {
    this.$noty.error(this.$tr('vacancy.preferences.modals.saveFailure.text'));
  }

  public go_to_copied_vacancy(id) {
    this.$router.push({
      name: 'vacancy_basic',
      params: {
        id
      }
    });
    this.get_vacancy(id);
  }

  public set_preview_url(val) {
    this.preview_url = val;
  }

  get vacancy() {
    return this.$store.getters['vacancy/one'];
  }

  get vacancyId() {
    return this.$store.getters['vacancy/one'].id;
  }

  get is_japan_locale() {
    return this.$store.getters['locale/is_japan_locale'];
  }

  get dateFormat() {
    return this.is_japan_locale ? 'YYYY.MM.DD' : 'DD.MM.YYYY';
  }

  get country() {
    return this.vacancy && this.vacancy.country ? this.vacancy.country : {};
  }

  set country(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        country: val
      }
    });
  }

  get is_active() {
    return this.vacancy.is_active;
  }

  get language() {
    return this.vacancy ? this.vacancy.language : null;
  }

  get change() {
    return this.$store.getters['vacancy/change'];
  }

  get isRoleEditScoring() {
    if (!this.vacancy || !this.vacancy.role) {
      return false;
    }

    return this.vacancy.role === 'role_edit_scoring';
  }

  get isDeleted() {
    const state = this.$store.state.vacancy;
    return state ? state.isDeleted : null;
  }

  get created_at() {
    if (!this.vacancy || !this.vacancy.created_at) {
      return '';
    }

    return Moment(this.vacancy.created_at, 'YYYY-MM-DD').format(this.dateFormat);
  }

  get isPrivate() {
    return this.vacancy ? this.vacancy.is_private : null;
  }

  set isPrivate(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        is_private: val
      }
    });
    // this.setChange(true);
  }

  get denyResponseDuplicates() {
    return this.vacancy ? this.vacancy.disable_duplicate_responses : null;
  }

  set denyResponseDuplicates(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        disable_duplicate_responses: val
      }
    });
  }

  get isResumeRequired() {
    return this.vacancy ? this.vacancy.is_resume_required : null;
  }

  set isResumeRequired(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        is_resume_required: val
      }
    });
    // this.setChange(true);
  }

  get isResumeMandatory() {
    return this.vacancy ? this.vacancy.is_resume_mandatory : null;
  }

  set isResumeMandatory(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        is_resume_mandatory: this.isResumeRequired ? val : false
      }
    });
  }

  get isDisableRecordVideointerview() {
    return this.vacancy ? this.vacancy.is_disable_record_videointerview : null;
  }

  set isDisableRecordVideointerview(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        is_disable_record_videointerview: val
      }
    });
    this.isPrivate = val;
    // this.setChange(true);
  }

  get name() {
    return this.vacancy ? this.vacancy.name : null;
  }

  set name(val) {
    this.$store.commit('vacancy/update', {
      model: this.vacancy,
      data: {
        name: val
      }
    });
  }

  get forbidden() {
    return this.$store.getters['vacancy/error'] !== null;
  }

  get has_changes() {
    return this.$store.getters['vacancy/change'];
  }

  get has_responses() {
    return this.vacancy ? this.vacancy.has_responses : false;
  }

  get subdomain() {
    if (!window.location || !window.location.host) {
      return '';
    }

    if (window.location.hostname.indexOf('release-ai.vcv') !== -1) {
      return 'ai';
    } else if (window.location.hostname.indexOf('release-ru.vcv') !== -1) {
      return 'ru';
    }

    return window.location.hostname ? window.location.hostname.split('.').pop() : '';
  }

  get active_application() {
    return this.modules.find(item => item.id === 'survey' && item.is_active);
  }

  get active_test() {
    return this.modules.find(item => item.id === 'test' && item.is_active);
  }

  mounted() {
    this.$parent.$on('saveOnParent', () => {
      this.$emit('saveOnParent');
    });
  }

  destroyed() {
    this.$parent.$off('saveOnParent');
  }
}
